import { useTranslation } from "react-i18next";
import { Btn, Icon, Pill, SectionHead } from "../../shared/ui";
import { GOALS, reachGoal } from "../../shared/lib/metrika";
import { formatPremiumPrice, usePublicSettings } from "../../shared/lib/usePublicSettings";
import { stagger } from "../../shared/motion";
import "./pricing.css";

const FEATURES = ["debt_book", "contracts", "payments", "analytics", "dashboard"] as const;

// Which features each tier includes (same order as FEATURES).
const INCLUDED = {
  pro: [true, true, true, true, true],
  free: [true, false, false, false, false],
} as const;

// «Narxlar» (prototype 266–307). Pro first and emphasised (ink card). Reveal is CSS only
// (`data-r`, armed by html.anim), so the prerender and the first client render are identical.
const Pricing = () => {
  const { t } = useTranslation();
  const settings = usePublicSettings();
  const premiumPrice = formatPremiumPrice(settings.premiumPrice);

  const features = (plan: "pro" | "free") => (
    <ul className="feats">
      {FEATURES.map((f, i) => {
        const on = INCLUDED[plan][i];
        return (
          <li key={f} className={on ? undefined : "off"} style={stagger(i)}>
            <span className="ck">
              <Icon name={on ? "i-check" : "i-x"} />
            </span>
            {t(`pricing.features.${f}`)}
          </li>
        );
      })}
    </ul>
  );

  return (
    <section className="sec" id="pricing">
      <div className="wrap">
        <SectionHead
          eyebrow={t("pricing.eyebrow")}
          title={t("pricing.title")}
          sub={t("pricing.subtitle")}
        />
        <div className="plans">
          <div className="plan pro" data-r>
            <div className="plan-h">
              <div>
                <h3 className="plan-name">{t("pricing.pro.rate")}</h3>
                <div className="plan-sub">{t("pricing.pro.payment_text")}</div>
              </div>
              <Pill tone="on-ink">{t("pricing.popular")}</Pill>
            </div>
            {/* Intro duration and monthly price come from the admin-managed public settings. */}
            <div>
              <div className="price">
                {t("pricing.pro.intro_headline", { months: settings.freeTierMonths })}
              </div>
              <div className="was">
                <s>
                  {premiumPrice} {t("pricing.per_month")}
                </s>{" "}
                {t("pricing.pro.after_intro")}
              </div>
            </div>
            {features("pro")}
            <Btn
              as="a"
              variant="primary"
              href={settings.supportTelegramLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => reachGoal(GOALS.telegram)}
            >
              {t("pricing.pro.btn_text", { months: settings.freeTierMonths })}
            </Btn>
          </div>

          <div className="plan" data-r style={stagger(1)}>
            <div className="plan-h">
              <div>
                <h3 className="plan-name">{t("pricing.free.rate")}</h3>
                <div className="plan-sub">{t("pricing.free.payment_text")}</div>
              </div>
            </div>
            <div>
              <div className="price">
                {t("pricing.free.price")}
                <small>{t("pricing.per_month")}</small>
              </div>
            </div>
            {features("free")}
            <Btn
              as="a"
              variant="secondary"
              href={settings.supportTelegramLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => reachGoal(GOALS.telegram)}
            >
              {t("pricing.free.btn_text")}
            </Btn>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
